import { useState } from "react";
import TableTemplate, {
  IAdditional,
  IAdditionalHeaderProps,
} from "../../TableTemplate/tableTemplate";
import { TableCell } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  deleteDeltaHedgeOpenOrders,
  deleteDeltaHedgeOrders,
  deltaHedgeOrderTablePause,
  deltaHedgeOrderTablePlay,
  getDeltaHedgeOrderBrokerOrderTable,
  getDeltaHedgeOrderChancesTable,
  getDeltaHedgeAggregationChart,
  getDeltaHedgeOrderDetailsOrderTable,
} from "../../../Requests/deltaHedgeOrder";
import { toast } from "react-toastify";
import { packetErrorHandler } from "../../../Utils/requestUtils";
import FullScreenLoading from "../../../Components/Loading/FullScreenLoading";
import TimelineIcon from "@mui/icons-material/Timeline";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PauseIcon from "@mui/icons-material/Pause";
import { IDeltaHedgeOrderTableReport } from "../../../Interfaces/deltaHedgeOrder";
import { useAppDispatch } from "../../../App/hooks";
import {
  setDeltaHedgeEditFlag,
  setDeltaHedgeEditRowValues,
} from "../../../Features/DeltaHedgeOrder/deltaHedgeOrderSlice";
import useRequest from "../../../Hooks/useRequest";
import { WrappedTimeSeriesChart } from "../../../Components/Timeseries/WrappedTimeSeriesChart";
import { CommonTableUI } from "../../TableTemplate/common";
import Modal from "../../../Components/Modal/Modal";
import HuntStrategyChancesTable from "../../Order/HuntChances/HuntStrategyChancesTable";
import Button from "../../../Components/Button";
import { OrderStatus } from "../../../Interfaces/orderInterfaces";
// Local Component
import { DeltaHedgeOrderTableAdditional } from "./Additional";

const DeltaHedgeAggregationTableAdditionalHeader = ({
  selectedRowsIds,
  setSelectedRowsIds,
  onAction,
  isSelectAll,
}: IAdditionalHeaderProps) => {
  const [loading, setLoading] = useState(false);

  const bulkDeleteAggregationHandler = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    deleteDeltaHedgeOpenOrders(selectedRowsIds, true)
      .then((resp) => {
        onAction();
        toast.success(resp.msg);
        setSelectedRowsIds([]);
      })
      .catch(packetErrorHandler);
  };

  const bulkPauseAggregationHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!selectedRowsIds.length) return;
    deltaHedgeOrderTablePause(selectedRowsIds, true)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler);
  };

  const bulkPlayAggregationHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!selectedRowsIds.length) return;
    deltaHedgeOrderTablePlay(selectedRowsIds, true)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler);
  };

  const cancelAllOpenAggregationsHandler = () => {
    if (!selectedRowsIds.length) return;
    setLoading(true);
    deleteDeltaHedgeOpenOrders(selectedRowsIds, true)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <>
      {loading ? <FullScreenLoading /> : <></>}
      <TableCell></TableCell>
      <TableCell>
        <div className="flex flex-row">
          <div
            onClick={selectedRowsIds.length ? bulkPauseAggregationHandler : undefined}
            className="hover:bg-slate-200 transition-all rounded p-1"
          >
            <PauseIcon
              className={`text-sky-900 cursor-pointer  ${
                selectedRowsIds.length ? "text-sky-900 " : "text-gray-300"
              } transition-all`}
            />
          </div>
          <div
            onClick={selectedRowsIds.length ? bulkPlayAggregationHandler : undefined}
            className="hover:bg-slate-200 transition-all rounded p-1"
          >
            <PlayCircleIcon
              className={`text-sky-900 cursor-pointer ${
                selectedRowsIds.length ? "text-sky-900 " : "text-gray-300"
              } transition-all`}
            />
          </div>
        </div>
      </TableCell>
      <TableCell></TableCell>

      <TableCell>
        <div
          onClick={selectedRowsIds.length ? cancelAllOpenAggregationsHandler : undefined}
          className={`text-xs py-1 px-2 rounded ${
            selectedRowsIds.length
              ? "bg-red-700 text-white cursor-pointer"
              : "bg-white text-gray-500 border-[1px] border-gray-300 cursor-not-allowed"
          }`}
        >
          حذف سفارشات باز
        </div>
      </TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell>
        <div
          className="hover:bg-red-100 transition-all rounded p-1"
          onClick={selectedRowsIds.length ? bulkDeleteAggregationHandler : undefined}
        >
          <DeleteIcon
            fontSize="small"
            className={`cursor-pointer ${
              selectedRowsIds.length ? "text-red-900 " : "text-gray-300"
            } transition-all`}
          />
        </div>
      </TableCell>
    </>
  );
};

const DeltaHedgeAggregationTableAdditionalCol: React.FC<{
  row: IDeltaHedgeOrderTableReport;
  onAction: () => void;
}> = ({ row, onAction }) => {
  const [isChancesOpen, setIsChancesOpen] = useState(false);
  const [isBrokerAggregationTableOpen, setIsBrokerAggregationTableOpen] =
    useState(false);
  const [isDetailsAggregationTableOpen, setIsDetailsAggregationTableOpen] =
    useState(false);
  const [isGraphModalOpen, setIsGraphModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  const editHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setDeltaHedgeEditRowValues({ ...row }));
    dispatch(setDeltaHedgeEditFlag(true));
  };

  const orderGraphRequest = useRequest(
    () => getDeltaHedgeAggregationChart(row.id),
    packetErrorHandler
  );

  const cancelAggregationHandler = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    deleteDeltaHedgeOrders([row.id], true)
      .then((resp) => {
        onAction();
        toast.success(resp.msg);
      })
      .catch(packetErrorHandler);
  };

  const pauseAggregationHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    deltaHedgeOrderTablePause([row.id])
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler);
  };

  const playAggregationHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    deltaHedgeOrderTablePlay([row.id])
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler);
  };

  const bulkPauseAggregationHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    deltaHedgeOrderTablePause([row.id], true)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler);
  };

  const bulkPlayAggregationHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    deltaHedgeOrderTablePlay([row.id], true)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler);
  };

  const getGraphModalUI = () => {
    if (!orderGraphRequest.response) return null;
    return (
      <div className="chart-fill-modal">
        <WrappedTimeSeriesChart dataset={orderGraphRequest.response} className="h-full w-full" />
      </div>
    );
  };

  const DeltaHedgeModalBtnClickHandler = () => {
    orderGraphRequest.fetchData();
    setIsGraphModalOpen(true);
  };

  const getBrokerAggregationTableUI = () => (
    <TableTemplate<{ id: number }, any>
      refreshTableTime={5000}
      getDataTable={(_, offset, pageSize) =>
        getDeltaHedgeOrderBrokerOrderTable(
          { conditionId: row.id },
          offset,
          pageSize
        )
      }
      TableUI={({ reports, onAction }) => (
        <CommonTableUI reports={reports} onAction={onAction} />
      )}
    />
  );
  const getDetailsAggregationTableUI = () => (
    <TableTemplate<{ id: number }, any>
      refreshTableTime={5000}
      getDataTable={(_, offset, pageSize) =>
        getDeltaHedgeOrderDetailsOrderTable(
          { conditionId: row.id },
          offset,
          pageSize
        )
      }
      TableUI={({ reports, onAction }) => (
        <CommonTableUI
          reports={reports}
          onAction={onAction}
          AdditionalBody={DeltaHedgeOrderTableAdditional}
        />
      )}
    />
  );

  return (
    <>
      {loading ? <FullScreenLoading /> : <></>}
      <TableCell>
        <div
          className="hover:bg-slate-300 transition-all rounded p-1"
          onClick={editHandler}
        >
          <EditIcon className="text-sky-700 cursor-pointer" />
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-row">
          <div
            onClick={bulkPauseAggregationHandler}
            className="hover:bg-slate-200 transition-all rounded p-1"
          >
            <PauseIcon
              className={`text-sky-900 cursor-pointer transition-all`}
            />
          </div>
          <div
            onClick={bulkPlayAggregationHandler}
            className="hover:bg-slate-200 transition-all rounded p-1"
          >
            <PlayCircleIcon
              className={`text-sky-900 cursor-pointer transition-all`}
            />
          </div>
        </div>
      </TableCell>
      <TableCell>
        {row.isActive ? (
          <div className="hover:bg-slate-300 transition-all rounded p-1">
            <PauseIcon
              className="text-sky-700 cursor-pointer"
              onClick={pauseAggregationHandler}
            />
          </div>
        ) : null}
        {row.isActive === false ? (
          <div className="hover:bg-slate-300 transition-all rounded p-1">
            <PlayCircleIcon
              className="text-sky-700 cursor-pointer"
              onClick={playAggregationHandler}
            />
          </div>
        ) : null}
      </TableCell>
      <TableCell>
        {isChancesOpen ? (
          <Modal title="شانس‌ها"
            UI={
              <HuntStrategyChancesTable
                getData={(_, offset, pageSize) =>
                  getDeltaHedgeOrderChancesTable(
                    { conditionId: row.id },
                    offset,
                    pageSize
                  )
                }
              />
            }
            onClose={() => setIsChancesOpen(false)}
          />
        ) : null}
        <Button
          onClick={() => setIsChancesOpen(true)}
          text="شانس ها"
          variant="contained"
          className="table-btn"
        />
      </TableCell>
      <TableCell>
        {isBrokerAggregationTableOpen ? (
          <Modal title="سفارشات"
            UI={getBrokerAggregationTableUI()}
            onClose={() => setIsBrokerAggregationTableOpen(false)}
          />
        ) : null}
        <Button
          onClick={() => setIsBrokerAggregationTableOpen(true)}
          text="سفارشات"
          variant="contained"
          className="table-btn"
        />
      </TableCell>
      <TableCell>
        {isDetailsAggregationTableOpen ? (
          <Modal title="جزئیات"
            UI={getDetailsAggregationTableUI()}
            onClose={() => setIsDetailsAggregationTableOpen(false)}
          />
        ) : null}
        <Button
          onClick={() => setIsDetailsAggregationTableOpen(true)}
          text="جزییات"
          variant="contained"
          className="table-btn"
        />
      </TableCell>
      <TableCell>
        <>
          {isGraphModalOpen ? (
            <Modal title="نمودار"
              onClose={() => setIsGraphModalOpen(false)}
              UI={getGraphModalUI()}
              fullScreen
            />
          ) : null}
          <Button
            onClick={DeltaHedgeModalBtnClickHandler}
            text={<TimelineIcon className="!text-base" />}
            variant="contained"
            className="table-btn"
          />
        </>
      </TableCell>
      <TableCell>
        {row.orderStatus !== OrderStatus.Cancelled ? (
          <div
            onClick={cancelAggregationHandler}
            className="hover:bg-red-100 transition-all rounded p-1"
          >
            <DeleteIcon
              fontSize="small"
              className="text-red-700 cursor-pointer"
            />
          </div>
        ) : null}
      </TableCell>
    </>
  );
};

export const DeltaHedgeAggregationAdditional: IAdditional<IDeltaHedgeOrderTableReport> =
  {
    getHeader: DeltaHedgeAggregationTableAdditionalHeader,
    getRow: DeltaHedgeAggregationTableAdditionalCol,
  };
